'use client'

import { useSearchParams } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { LuSearch, LuX } from 'react-icons/lu'

import { Button } from '@/core/components/form/button/button'
import { Combobox } from '@/core/components/form/combobox'
import { InputText } from '@/core/components/form/input-text'

import SearchResultCard from '@/features/veluvit/components/search/search-result-card'
import PageHeader from '@/features/veluvit/components/shell/page-header'
import { TabRow } from '@/features/veluvit/components/ui/layout-parts'
import EmptyState from '@/features/veluvit/components/ui/empty-state'
import { useShowAdult } from '@/features/veluvit/hooks/use-show-adult'
import { searchWorks } from '@/features/veluvit/services/search.service'
import {
  searchQuerySchema,
  searchPageSchema,
  searchPath,
  validateSearchPage,
} from '@/features/veluvit/schemas/search.schema'
import { useCachedSWR } from '@/core/lib/swr/use-cached-swr'
import { appSwrKey } from '@/features/veluvit/lib/app-read'
import { useAppReadScope } from '@/features/veluvit/hooks/use-app-read-scope'
import { useSessionStore } from '@/features/veluvit/stores/session.store'

import type { Route } from 'next'
import type {
  SearchQuery,
  SearchWork,
  SearchPage,
} from '@/features/veluvit/schemas/search.schema'
import type { FetcherResponse } from '@/core/lib/fetcher/core'

type SearchState = {
  scope: string
  status: 'idle' | 'loading' | 'ready' | 'error'
  works: SearchWork[]
  total: number
  nextOffset: number | null
  error: string
  errorCode?: string
  loadingMore: boolean
  moreError: string
}

const emptyState = (
  scope: string,
  status: SearchState['status'] = 'idle'
): SearchState => ({
  scope,
  status,
  works: [],
  total: 0,
  nextOffset: null,
  error: '',
  loadingMore: false,
  moreError: '',
})

const KINDS = [
  { key: 'all', label: '전체' },
  { key: 'character', label: '캐릭터' },
  { key: 'story', label: '스토리' },
] as const
const SORTS = [
  { value: 'recommended', label: '추천순' },
  { value: 'popular', label: '인기순' },
  { value: 'new', label: '최신순' },
]

function searchUrl(query: SearchQuery): Route {
  const params = new URLSearchParams({ kind: query.kind, sort: query.sort })
  if (query.q) params.set('q', query.q)
  return `/search?${params}` as Route
}

function failure(error: unknown): { message: string; code?: string } {
  if (!(error instanceof Error))
    return { message: '검색 결과를 불러오지 못했어요.' }
  return {
    message: error.message,
    ...('code' in error && typeof error.code === 'string'
      ? { code: error.code }
      : {}),
  }
}

function requireAllowedWorks(
  works: SearchWork[],
  showAdult: boolean,
  eligible: boolean
) {
  if (
    (!showAdult || !eligible) &&
    works.some((work) => work.style?.rating === 'unsafe')
  ) {
    throw new Error(
      '세이프 모드가 적용된 검색 결과를 확인하지 못했어요. 다시 시도해 주세요.'
    )
  }
}

/** URL에 확정한 검색어를 서버에서 검색한다. 입력 중인 글은 제출 전까지 요청하지 않는다. */
export default function SearchScreen({
  invalidUrl = false,
  epoch,
  initialPath,
  initial,
}: {
  invalidUrl?: boolean
  epoch?: string
  initialPath?: string
  initial?: FetcherResponse<SearchPage>
}) {
  const params = useSearchParams()
  const rawQ = params.get('q') ?? ''
  const rawKind = params.get('kind') ?? 'all'
  const rawSort = params.get('sort') ?? 'recommended'
  const parsed = searchQuerySchema.safeParse({
    q: rawQ,
    kind: rawKind,
    sort: rawSort,
  })
  const query = !invalidUrl && parsed.success ? parsed.data : null
  const kind: SearchQuery['kind'] =
    rawKind === 'character' || rawKind === 'story' ? rawKind : 'all'
  const sort: SearchQuery['sort'] =
    rawSort === 'popular' || rawSort === 'new' ? rawSort : 'recommended'
  const identity = useSessionStore((state) => state.identity)
  const eligible = useSessionStore(
    (state) => !!state.data?.config?.adult?.eligible
  )
  const showAdult = useShowAdult()
  const scope = JSON.stringify([
    rawQ,
    rawKind,
    rawSort,
    identity,
    showAdult,
    eligible,
  ])
  const [draft, setDraft] = useState(rawQ.slice(0, 100))
  const [inputError, setInputError] = useState('')
  const [view, setView] = useState<SearchState>(() => emptyState(scope))
  const generation = useRef(0)
  const safety = useRef([showAdult, eligible])
  const moreController = useRef<AbortController | null>(null)
  const morePending = useRef(false)
  const composing = useRef(false)
  const input = useRef<HTMLInputElement>(null)
  const readScope = useAppReadScope()
  const path = query?.q
    ? (() => {
        try {
          return searchPath(query)
        } catch {
          return null
        }
      })()
    : null
  const key = readScope && path ? appSwrKey(readScope, path) : null
  const { data, error, mutate } = useCachedSWR<SearchPage>(key, 'REALTIME', {
    responseSchema: searchPageSchema,
    ...(readScope === epoch && initialPath === path && initial
      ? {
          fallbackData: initial,
          ...(initial.ok ? {} : { revalidateOnMount: false }),
        }
      : {}),
  })
  let first: SearchState = emptyState(scope)
  if (!query)
    first = {
      ...emptyState(scope, 'error'),
      error: '검색 주소를 확인해 주세요. 검색어는 100자까지 입력할 수 있어요.',
      errorCode: 'INVALID_SEARCH_URL',
    }
  else if (path === null && query.q)
    first = {
      ...emptyState(scope, 'error'),
      error: '태그 하나를 공백 없이 입력해 주세요.',
      errorCode: 'SEARCH_TAG_INVALID',
    }
  else if (query.q && !data && !error) first = emptyState(scope, 'loading')
  else if (query.q && (error || data?.ok === false))
    first = {
      ...emptyState(scope, 'error'),
      error:
        error?.message || data?.message || '검색 결과를 불러오지 못했어요.',
    }
  else if (query.q && data?.ok) {
    try {
      const page = validateSearchPage(data.data, query)
      requireAllowedWorks(page.works, showAdult, eligible)
      first = {
        ...emptyState(scope, 'ready'),
        works: page.works,
        total: page.total,
        nextOffset: page.nextOffset,
      }
    } catch (cause) {
      first = {
        ...emptyState(scope, 'error'),
        error: failure(cause).message,
        errorCode: failure(cause).code,
      }
    }
  }
  const current = view.scope === scope && view.status === 'ready' ? view : first

  const sameBoundary = () => {
    const session = useSessionStore.getState()
    const nextEligible = !!session.data?.config?.adult?.eligible
    const nextAdult = !!session.data?.user?.settings?.showAdult && nextEligible
    return (
      session.identity === identity &&
      nextAdult === showAdult &&
      nextEligible === eligible
    )
  }

  const [seenRawQ, setSeenRawQ] = useState(rawQ)
  if (seenRawQ !== rawQ) {
    setSeenRawQ(rawQ)
    setDraft(rawQ.slice(0, 100))
    setInputError('')
  }
  if (view.scope !== scope) setView(emptyState(scope))

  useEffect(() => {
    if (safety.current[0] !== showAdult || safety.current[1] !== eligible) {
      safety.current = [showAdult, eligible]
      if (key) void mutate()
    }
  }, [showAdult, eligible, key, mutate])

  useEffect(() => {
    generation.current += 1
    moreController.current?.abort()
    morePending.current = false
  }, [scope])

  const updateUrl = (next: SearchQuery) =>
    window.history.pushState(null, '', searchUrl(next))
  const clear = () => {
    generation.current += 1
    moreController.current?.abort()
    morePending.current = false
    setDraft('')
    setInputError('')
    setView(emptyState(scope))
    window.history.replaceState(null, '', '/search')
  }
  const submit = () => {
    const next = searchQuerySchema.safeParse({ q: draft.trim(), kind, sort })
    if (!next.success) {
      setInputError('검색어는 100자까지 입력할 수 있어요.')
      return
    }
    if (!next.data.q) {
      clear()
      return
    }
    setInputError('')
    if (
      query &&
      next.data.q === query.q &&
      next.data.kind === query.kind &&
      next.data.sort === query.sort
    ) {
      void mutate()
      return
    }
    updateUrl(next.data)
  }
  const loadMore = () => {
    if (
      !query?.q ||
      current.status !== 'ready' ||
      current.nextOffset === null ||
      morePending.current
    )
      return
    const offset = current.nextOffset
    const request = generation.current
    const controller = new AbortController()
    moreController.current = controller
    morePending.current = true
    setView({ ...current, loadingMore: true, moreError: '' })
    searchWorks(query, offset, controller.signal)
      .then((page) => {
        if (
          controller.signal.aborted ||
          request !== generation.current ||
          !sameBoundary()
        )
          return
        requireAllowedWorks(page.works, showAdult, eligible)
        setView((previous) => {
          if (previous.scope !== scope) return previous
          const seen = new Set(previous.works.map((work) => work.id))
          const more = page.works.filter((work) => !seen.has(work.id))
          return {
            ...previous,
            works: [...previous.works, ...more],
            total: page.total,
            nextOffset: page.nextOffset,
            loadingMore: false,
            moreError: '',
          }
        })
      })
      .catch((error: unknown) => {
        if (
          controller.signal.aborted ||
          request !== generation.current ||
          !sameBoundary()
        )
          return
        setView((previous) =>
          previous.scope === scope
            ? {
                ...previous,
                loadingMore: false,
                moreError: failure(error).message,
              }
            : previous
        )
      })
      .finally(() => {
        if (moreController.current === controller) {
          morePending.current = false
          moreController.current = null
        }
      })
  }

  return (
    <>
      <PageHeader back title="검색" />
      <main className="px-4 pb-12 over10:px-8">
        <form
          role="search"
          className="mt-5 flex w-full max-w-150 items-start gap-2 over10:mt-7"
          onSubmit={(event) => {
            event.preventDefault()
            if (!composing.current) submit()
          }}
        >
          <div className="relative min-w-0 flex-1">
            <InputText
              ref={input}
              type="search"
              name="q"
              value={draft}
              maxLength={100}
              aria-label="검색어"
              placeholder="작품·캐릭터 이름, #태그 검색"
              error={inputError}
              className="pr-12 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
              onChange={(event) => {
                setDraft(event.target.value)
                if (inputError) setInputError('')
              }}
              onCompositionStart={() => {
                composing.current = true
              }}
              onCompositionEnd={() => {
                composing.current = false
              }}
              onKeyDown={(event) => {
                if (
                  event.key === 'Enter' &&
                  (composing.current || event.nativeEvent.isComposing)
                )
                  event.preventDefault()
              }}
            />
            {draft && (
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0.5 top-0.5"
                aria-label="검색어 지우기"
                onClick={() => {
                  clear()
                  input.current?.focus()
                }}
              >
                <LuX aria-hidden="true" className="size-4" />
              </Button>
            )}
          </div>
          <Button
            type="submit"
            variant="primary"
            size="md"
            aria-label="검색"
            className="gap-2"
          >
            <LuSearch aria-hidden="true" className="size-4" />
            <span className="hidden over5:inline">검색</span>
          </Button>
        </form>
        <div className="-mx-4 mt-5 over10:-mx-8">
          <TabRow
            wide
            items={KINDS.map((item) => ({
              key: item.key,
              label: item.label,
              on: kind === item.key,
              onClick: () =>
                updateUrl({ q: query?.q ?? '', kind: item.key, sort }),
            }))}
          />
        </div>
        {query?.q && (
          <div className="mt-4 flex min-h-11 items-center justify-between gap-3">
            <span className="text-c13 text-t3 tabular-nums over10:text-c14">
              {current.status === 'ready'
                ? `검색 결과 ${current.total.toLocaleString()}개`
                : current.status === 'loading'
                  ? '검색 중…'
                  : ''}
            </span>
            <Combobox
              variant="ghost"
              ariaLabel="검색 결과 정렬"
              value={sort}
              options={SORTS}
              onChange={(value) =>
                updateUrl({
                  q: query.q,
                  kind,
                  sort: value as SearchQuery['sort'],
                })
              }
            />
          </div>
        )}
        {current.status === 'idle' ? (
          <EmptyState
            title="찾고 싶은 작품을 검색해 주세요"
            description="이름이나 #태그를 입력해 주세요."
            icon={<LuSearch />}
          />
        ) : current.status === 'loading' ? (
          <p role="status" className="py-16 text-center text-c14 text-t3">
            검색 결과를 불러오는 중이에요…
          </p>
        ) : current.status === 'error' ? (
          <EmptyState
            title={
              current.errorCode === 'SEARCH_KIND_UNSUPPORTED'
                ? '이 분류는 아직 검색할 수 없어요'
                : '검색 결과를 불러오지 못했어요'
            }
            description={current.error}
            action={
              current.errorCode === 'SEARCH_KIND_UNSUPPORTED' ? (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() =>
                    updateUrl({ q: query?.q ?? '', kind: 'all', sort })
                  }
                >
                  전체에서 검색
                </Button>
              ) : current.errorCode === 'INVALID_SEARCH_URL' ? (
                <Button variant="secondary" size="sm" onClick={clear}>
                  검색어 지우기
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => void mutate()}
                >
                  다시 시도
                </Button>
              )
            }
          />
        ) : current.works.length === 0 ? (
          <EmptyState
            title="검색 결과가 없어요"
            description="다른 검색어로 다시 찾아보세요."
          />
        ) : (
          <>
            <ul className="mt-4 grid list-none grid-cols-2 gap-x-3 gap-y-7 p-0 over6:grid-cols-3 over10:grid-cols-4 over13:grid-cols-5 over16:grid-cols-6 over10:gap-x-4">
              {current.works.map((work) => (
                <li key={work.id} className="min-w-0">
                  <SearchResultCard work={work} />
                </li>
              ))}
            </ul>
            {current.moreError && (
              <div role="alert" className="mt-6 text-center">
                <p className="text-c13 text-error">{current.moreError}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-3"
                  onClick={loadMore}
                >
                  다시 시도
                </Button>
              </div>
            )}
            {current.nextOffset !== null && !current.moreError && (
              <div className="mt-8 text-center">
                <Button
                  variant="secondary"
                  size="md"
                  loading={current.loadingMore}
                  disabled={current.loadingMore}
                  onClick={loadMore}
                >
                  더 보기
                </Button>
              </div>
            )}
          </>
        )}
      </main>
    </>
  )
}
